import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Mail, MessageSquare, MapPin, Sparkles } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useC, useDark } from '@/features/shared/theme/kaziColors';
import { DanaAvatar } from '@/features/shared/components/branding/DanaAvatar';
import { openDana } from '@/lib/dana';
import { conversationsApi } from '@/features/conversations/api/conversationsApi';
import { buildTenantPath, extractTenantSlug } from '@/features/navigation/utils';

interface Employee {
  id: string;
  userId?: string;
  name: string;
  position: string;
  department: string;
  email: string;
  phone: string;
  status: 'active' | 'on_leave' | 'inactive';
  avatar?: string;
  hireDate: string;
  managerId?: string;
  isAssistant?: boolean;
  isDraft?: boolean;
  incompleteCount?: number;
}

const AV_COLORS = ['#F28A3A', '#3B82F6', '#22C55E', '#8B5CF6', '#EF4444', '#F6B26B'];

// v5 design-system employee list item — inline styles, theme-aware.
export const EmployeeListItem: React.FC<{ employee: Employee }> = ({ employee }) => {
  const { t } = useTranslation(['employees']);
  const C = useC();
  const dark = useDark();
  const navigate = useNavigate();
  const location = useLocation();
  const [hov, setHov] = useState(false);

  // Open (or create) a direct message with this employee (Conversations). Someone with
  // no login account can't receive in-app messages → point to email, not Dana.
  const openDM = async () => {
    if (!employee.userId) {
      toast(
        employee.email
          ? t('employees:directory.no_account_email', { name: employee.name, defaultValue: `${employee.name} doesn't have a KaziPeople login yet — email them instead.` })
          : t('employees:directory.no_account', { name: employee.name, defaultValue: `${employee.name} doesn't have a KaziPeople login yet, so they can't receive in-app messages.` }),
        { icon: 'ℹ️' },
      );
      return;
    }
    try {
      const conv = await conversationsApi.createDirect(employee.userId, employee.id);
      const slug = extractTenantSlug(location.pathname);
      navigate(buildTenantPath(`messages/${conv.id}`, slug));
    } catch {
      toast.error(t('employees:directory.message_failed', { defaultValue: 'Could not open the conversation.' }));
    }
  };

  // Draft outranks the mapped status (a Draft is stored as a non-active record but
  // maps to 'active' in the UI union) — surface it as a distinct "to activate" pill
  // so incomplete hires are never mistaken for Active.
  const statusCfg = employee.isDraft
    ? {
        bg: C.yellowLight, color: C.yellowText, dot: C.yellow,
        label: employee.incompleteCount
          ? t('employees:status.draft_count', { defaultValue: 'Draft · {{count}} to activate', count: employee.incompleteCount })
          : t('employees:status.draft', { defaultValue: 'Draft · incomplete' }),
      }
    : {
        active: { bg: C.greenLight, color: C.greenText, dot: C.green, label: t('employees:status.active', { defaultValue: 'Active' }) },
        on_leave: { bg: C.yellowLight, color: C.yellowText, dot: C.yellow, label: t('employees:status.on_leave', { defaultValue: 'On Leave' }) },
        inactive: { bg: C.redLight, color: C.redText, dot: C.red, label: t('employees:status.inactive', { defaultValue: 'Inactive' }) },
      }[employee.status] || { bg: C.creamDark, color: C.charcoalMid, dot: C.charcoalSubtle, label: '' };

  const goProfile = () => {
    const base = location.pathname.replace(/\/employees(\/.*)?$/, '/employees');
    navigate(`${base}/${employee.id}`);
  };
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const initials = (employee.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const avColor = AV_COLORS[(employee.name || '?').charCodeAt(0) % AV_COLORS.length];

  const iconBtn = (primary?: boolean): React.CSSProperties => ({
    width: 36, height: 36, borderRadius: 8,
    border: primary ? 'none' : `1px solid ${C.border}`,
    background: primary ? C.orange : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  });

  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} onClick={goProfile}
      style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', borderRadius: 12,
        background: hov ? (dark ? C.creamDeep : C.cream) : 'transparent',
        border: `1px solid ${hov ? C.borderMed : 'transparent'}`, transition: 'all 0.2s', cursor: 'pointer',
      }}
    >
      <div style={{ position: 'relative' }}>
        {employee.isAssistant ? (
          <DanaAvatar size={48} />
        ) : employee.avatar ? (
          <img src={employee.avatar} alt={employee.name} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: avColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, color: '#fff' }}>{initials}</div>
        )}
        {!employee.isAssistant && (
          <div style={{ position: 'absolute', bottom: 0, right: 0, width: 14, height: 14, borderRadius: '50%', background: statusCfg.dot, border: `2px solid ${dark ? C.creamDeep : C.white}` }} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: C.charcoal, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          {employee.name}
          {employee.isAssistant && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 6px', borderRadius: 99, background: 'rgba(242,138,58,0.12)', color: C.orange, fontSize: 9, fontWeight: 700 }}>
              <Sparkles size={8} /> AI
            </span>
          )}
        </h4>
        <p style={{ fontSize: 12, color: C.charcoalLight, marginBottom: 2 }}>{employee.position}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: C.charcoalSubtle }}>{employee.department}</span>
          {employee.email ? (
            <>
              <span style={{ fontSize: 11, color: C.charcoalSubtle }}>•</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPin size={10} color={C.charcoalSubtle} />
                <span style={{ fontSize: 11, color: C.charcoalSubtle }}>{employee.email}</span>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={stop}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 99, background: employee.isAssistant ? 'rgba(242,138,58,0.12)' : statusCfg.bg, color: employee.isAssistant ? C.orange : statusCfg.color, fontSize: 10, fontWeight: 600 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: employee.isAssistant ? C.orange : statusCfg.dot }} />
          {employee.isAssistant ? t('employees:directory.always_available', { defaultValue: 'Always available' }) : statusCfg.label}
        </span>
        {employee.email ? (
          <a href={`mailto:${employee.email}`} onClick={() => { navigator.clipboard?.writeText(employee.email).then(() => toast.success(t('employees:directory.email_copied', { defaultValue: 'Email copied' }))).catch(() => {}); }}
            style={iconBtn()} title={`Email ${employee.email}`}><Mail size={14} color={C.charcoalMid} /></a>
        ) : (
          <button style={{ ...iconBtn(), opacity: 0.45, cursor: 'not-allowed' }} title={t('employees:directory.no_email', { defaultValue: 'No email on file' })} type="button" disabled><Mail size={14} color={C.charcoalSubtle} /></button>
        )}
        <button style={iconBtn(true)} title={employee.isAssistant ? t('employees:directory.chat_with_dana', { defaultValue: 'Chat with Dana' }) : t('employees:directory.message', { defaultValue: `Message ${employee.name}` })}
          type="button" onClick={employee.isAssistant ? () => openDana() : openDM}><MessageSquare size={14} color="#fff" /></button>
      </div>
    </div>
  );
};

export default EmployeeListItem;
